import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { BrandLockup } from "./Brand";

export default function Header() {
  const { t, locale, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onHome = pathname === "/";
  const transparent = onHome && !scrolled;

  const links = [
    { to: "/", label: t.nav.home, id: "home", end: true },
    { to: "/solutions", label: t.nav.capabilities, id: "solutions" },
    { to: "/sectors", label: t.nav.sectors, id: "sectors" },
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-obsidian/95 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <BrandLockup inverted />

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-champagne transition-colors"
            aria-label="Toggle language"
          >
            <Globe2 size={14} />
            <span>{locale === "en" ? "EN / PT" : "PT / EN"}</span>
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-obsidian border-t border-white/10"
          data-testid="mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.end}
                data-testid={`mobile-nav-${l.id}`}
                className={({ isActive }) =>
                  `nav-link text-sm py-1 ${isActive ? "active" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={toggle}
              data-testid="mobile-lang-toggle"
              className="self-start font-mono text-[11px] uppercase tracking-[0.22em] text-white/70 inline-flex items-center gap-2"
            >
              <Globe2 size={14} />
              {locale === "en" ? "Português" : "English"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
